import { useEffect, useState, useCallback, useRef } from "react";
import { Upload, Loader2, Trash2, X, Link2, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import { money, downloadDoc } from "./shared";
import { useI18n } from "../../../context/I18nContext";

const STATUS_CLS = {
  validado: "bg-emerald-500/10 text-emerald-600",
  pendiente: "bg-gold/15 text-gold",
};

export default function Tickets({ companyId, year, month, onDataChange }) {
  const { t } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [picker, setPicker] = useState(null); // {mode:'link'|'unify', ticketId?}
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!companyId) return;
    const p = new URLSearchParams({ company_id: companyId });
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    api.get(`/accounting/tickets?${p}`).then((r) => setTickets(r.data)).catch(() => {});
  }, [companyId, year, month]);
  useEffect(() => { load(); setSelected([]); }, [load]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("file", file);
      try { await api.post("/accounting/tickets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (e) { toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || t("os.contab.err")}`); }
    }
    setUploading(false);
    if (ok) toast.success(t("os.contab.ticketsUploaded")(ok));
    if (fileRef.current) fileRef.current.value = "";
    load(); onDataChange?.();
  };
  const del = async (id) => { if (!window.confirm(t("os.contab.delTicketConfirm"))) return; try { await api.delete(`/accounting/tickets/${id}`); load(); } catch { toast.error(t("os.contab.err")); } };
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const doLink = async (docId) => {
    try {
      if (picker.mode === "link") { await api.post(`/accounting/tickets/${picker.ticketId}/link`, { doc_id: docId }); toast.success(t("os.contab.ticketLinked")); }
      else {
        const { data } = await api.post("/accounting/tickets/unify", { ticket_ids: selected, doc_id: docId });
        if (data.coincide) toast.success(t("os.contab.unifiedOk")(data.tickets, money(data.total_factura)));
        else toast.warning(t("os.contab.unifiedMismatch")(money(data.suma_tickets), money(data.total_factura)));
        setSelected([]);
      }
      setPicker(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.contab.err")); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-navy/50">{t("os.contab.ticketIntro")}</p>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button data-testid="ticket-unify-btn" onClick={() => setPicker({ mode: "unify" })}
              className="inline-flex items-center gap-2 rounded-full border border-violet text-violet text-sm px-4 py-3 hover:bg-violet/5 transition-colors">
              <Layers className="w-4 h-4" /> {t("os.contab.unifyBtn")(selected.length)}
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple hidden data-testid="ticket-file-input" onChange={(e) => onFiles(e.target.files)} />
          <button data-testid="ticket-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("os.contab.uploadTickets")}
          </button>
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className="text-navy/40 py-12 text-center border border-dashed border-navy/15 rounded-lg" data-testid="ticket-empty">
          {t("os.contab.emptyTickets")}
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="tickets-panel">
          <div className="hidden md:grid grid-cols-[40px_95px_1.4fr_110px_150px_1fr_60px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
            <span /><span>{t("os.contab.ticketCols.fecha")}</span><span>{t("os.contab.ticketCols.comercio")}</span><span>{t("os.contab.ticketCols.monto")}</span><span>{t("os.contab.ticketCols.estatus")}</span><span>{t("os.contab.ticketCols.factura")}</span><span /></div>
          {tickets.map((tk) => (
            <div key={tk.id} data-testid={`ticket-${tk.id}`} className="grid grid-cols-2 md:grid-cols-[40px_95px_1.4fr_110px_150px_1fr_60px] gap-3 px-5 py-4 items-center border-b border-navy/5 last:border-0 text-sm">
              <input type="checkbox" data-testid={`ticket-check-${tk.id}`} checked={selected.includes(tk.id)} onChange={() => toggle(tk.id)} className="w-4 h-4 accent-violet" />
              <div className="text-xs text-navy/50">{(tk.date || "").slice(0, 10)}</div>
              <div className="min-w-0"><div className="font-medium truncate">{tk.merchant || "—"}</div><div className="font-mono text-[11px] text-navy/40">{tk.rfc_emisor}</div></div>
              <div className="font-medium">{money(tk.amount)}</div>
              <div><span className={`text-[11px] px-2 py-1 rounded-full ${STATUS_CLS[tk.status] || STATUS_CLS.pendiente}`}>{t(`os.contab.ticketStatus.${tk.status}`) || tk.status}</span></div>
              <div className="text-xs">
                {tk.matched_doc_id ? <span className="text-emerald-600 flex items-center gap-1">{tk.group_id ? t("os.contab.unified") : t("os.contab.linkedShort")}</span> :
                  <button data-testid={`ticket-link-${tk.id}`} onClick={() => setPicker({ mode: "link", ticketId: tk.id })} className="inline-flex items-center gap-1 text-violet hover:underline"><Link2 className="w-3.5 h-3.5" /> {t("os.contab.linkInvoice")}</button>}
              </div>
              <div className="flex items-center gap-2 justify-self-end">
                {tk.storage_path && <button data-testid={`ticket-download-${tk.id}`} title={t("os.contab.download")} onClick={() => downloadDoc("ticket", tk.id, tk.original_filename)} className="text-navy/30 hover:text-violet"><Download className="w-4 h-4" /></button>}
                <button data-testid={`ticket-del-${tk.id}`} onClick={() => del(tk.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>            </div>
          ))}
        </div>
      )}

      {picker && <InvoicePicker companyId={companyId} onClose={() => setPicker(null)} onPick={doLink} mode={picker.mode} selectedCount={selected.length} />}
    </div>
  );
}

function InvoicePicker({ companyId, onClose, onPick, mode, selectedCount }) {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get(`/accounting/invoices?company_id=${companyId}&category=egreso`).then((r) => setInvoices(r.data)).catch(() => {});
  }, [companyId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="ticket-invoice-picker" className="w-full max-w-lg bg-cream rounded-xl p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{mode === "unify" ? t("os.contab.pickUnify")(selectedCount) : t("os.contab.pickLink")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto space-y-2">
          {invoices.length === 0 ? <div className="text-navy/40 text-sm py-6 text-center">{t("os.contab.noEgreso")}</div> :
            invoices.map((inv) => (
              <button key={inv.id} data-testid={`picker-invoice-${inv.id}`} onClick={() => onPick(inv.id)}
                className="w-full text-left bg-white border border-navy/10 rounded-lg px-4 py-3 hover:border-violet transition-colors">
                <div className="flex justify-between text-sm"><span className="font-medium truncate">{inv.emisor_nombre || "—"}</span><span className="font-medium shrink-0 ml-2">{money(inv.total)}</span></div>
                <div className="text-xs text-navy/40">{(inv.fecha || "").slice(0, 10)} · {inv.emisor_rfc}</div>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
